import { type CostCenter, type Mandator } from '@einwert/valuation-api-client'
import {
    Alert,
    CreateOrderSuccessLottie,
    DocumentIcon,
    EmptyFilesImage,
    InfoBox,
    NumberStepLabel,
    PageGridContainer,
    PageGridItem,
    sendToSentry,
    Stepper,
    SuccessBackdrop,
    tokens,
} from 'ComponentLibrary'
import { useReducer, useState } from 'react'
import { AssetsData, OnSubmit, type StepProps } from './types'
import type { CreateNewOrder } from 'ClientPortal/src/pages/Orders/CreateOrder/types'
import type { CustomerUser } from '@einwert/valuation-api-client'
import type { ExistingOrder } from '@einwert/valuation-api-client'
import { useNavigate } from 'react-router-dom'
import { Stack, Step } from '@mui/material'
import { orderReducer } from 'ClientPortal/src/pages/Orders/CreateOrder/orderReducer'
import { useCreateOrderPage } from 'ClientPortal/src/pages/Orders/CreateOrder/CreateOrderPage.data'
import { MandatorStep } from 'ClientPortal/src/pages/Orders/CreateOrder/MandatorStep/MandatorStep'
import { AssetStep } from 'ClientPortal/src/pages/Orders/CreateOrder/AssetStep/AssetStep'
import { OrderValuationStep } from 'ClientPortal/src/pages/Orders/CreateOrder/OrderValuationStep/OrderValuationStep'
import { isEmpty } from 'lodash'
import { STATIC_APP_PATHS } from 'ClientPortal/src/router/constants'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { useFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import { Typography } from 'ComponentLibrary/src/piazza'
import { InfoIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import invariant from 'tiny-invariant'

export function CreateOrderPage({ isCreatingFromAsset }: { isCreatingFromAsset?: boolean }) {
    const { isCustomerFineGrainedAuthorizationEnabled } = useFlags()

    if (isCustomerFineGrainedAuthorizationEnabled) {
        return <CreateOrderInfoPage />
    }

    return <CreateOrderFormPage isCreatingFromAsset={isCreatingFromAsset} />
}

function CreateOrderInfoPage() {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.createOrderPage' })

    return (
        <PageGridContainer withBottomSpace withTopSpace withFooter>
            <PageGridItem columnSpan={6} columnSpanXs={4}>
                <Headline title={t('title')} variant="headline1" withSpacing />
            </PageGridItem>
            <PageGridItem columnSpan={6} columnSpanXs={4}>
                <Stack direction="column" alignItems="center" gap={tokens.SizingSpacing400} maxWidth="420px">
                    <EmptyFilesImage height="270px" width="270px" />
                    <InfoBox variant="ordinary" title={t('infoTitle')} icon={<InfoIcon />}>
                        <Stack direction="column" gap={tokens.SizingSpacing075}>
                            <Typography variant="textS" color={tokens.ColorTextBold}>
                                {t('missingPermission')}{' '}
                                <a style={{ color: tokens.ColorGraphSequentialTwoDarkest }} href="mailto:auftrag@einwert.com">
                                    auftrag@einwert.com
                                </a>
                                .
                            </Typography>
                            <Typography variant="textS">{t('futureAccess')}</Typography>
                        </Stack>
                    </InfoBox>
                </Stack>
            </PageGridItem>
        </PageGridContainer>
    )
}

function CreateOrderFormPage({ isCreatingFromAsset }: { isCreatingFromAsset?: boolean }) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.createOrderPage' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const navigate = useNavigate()
    const [activeStep, setActiveStep] = useState<number>(0)
    const [newOrderId, setNewOrderId] = useState<string | undefined>(undefined)

    const handleNext = () => {
        window.scrollTo({ top: 0, left: 0 })
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }

    const handleBack = () => {
        window.scrollTo({ top: 0, left: 0 })
        setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }

    const onCreateSuccess = (data: ExistingOrder) => setNewOrderId(data.uuid)
    const closeSuccessMsg = () => navigate(`/orders/${newOrderId}/overview`)

    const {
        user,
        customer,
        getPrefilledMandators,
        getPrefilledCostCenters,
        isMandatorsInitialLoading,
        isCostCentersInitialLoading,
        assetsData,
        createOrderMutate,
        isCreatingOrder,
        isCreateOrderError,
        patchCustomer,
        isPatchingCustomer,
        fetchCustomer,
        isFetchingCustomer,
        customerUsers,
        isCustomerUsersPending,
        assetId,
    } = useCreateOrderPage({
        onCreateSuccess,
    })
    const [order, dispatch] = useReducer(orderReducer, {
        contacts: [],
        asset_uuid: assetId,
        valuation_reason: '',
        address: {
            country: '',
            city: '',
            street: '',
            state: '',
            zip: '',
        },
        mandators: [],
        asset_usage_types: [],
        assigned_customer_users: user ? [user.id] : [],
    })

    const onSubmit = (order: CreateNewOrder) => {
        invariant(customer?.uuid, 'Cannot create order without customer')
        createOrderMutate({ ...order, customer_uuid: customer.uuid })
    }
    const onCancel = () => navigate(isCreatingFromAsset && assetId ? `/assets/${assetId}/valuations` : STATIC_APP_PATHS.orderListPage)

    const prefilledMandators = getPrefilledMandators()
    const prefilledCostCenters = getPrefilledCostCenters()

    if (isEmpty(order.mandators) && prefilledMandators.length === 1) {
        dispatch({
            type: 'addMandator',
            payload: prefilledMandators[0],
        })
    }

    const createNewCostCenter = async (newCostCenter: Omit<CostCenter, 'uuid'>) => {
        await patchCustomer(
            [
                {
                    op: 'add',
                    path: '/company_profile/cost_centers/-',
                    value: newCostCenter,
                },
            ],
            {
                onSuccess: async () => {
                    try {
                        const updatedCustomer = await fetchCustomer()
                        const newCostCenter = updatedCustomer?.company_profile?.cost_centers.at(-1)
                        newCostCenter && dispatch({ type: 'updateCostCenter', payload: newCostCenter.uuid })
                    } catch (error: unknown) {
                        sendToSentry(error)
                    }
                },
            }
        )
    }

    const isCreatingNewCostCenter = isPatchingCustomer || isFetchingCustomer

    const steps = getSteps({
        order,
        customerUsers,
        isCustomerUsersPending,
        dispatch,
        handleBack,
        handleNext,
        activeStep,
        onSubmit,
        prefilledCostCenters,
        prefilledMandators,
        isMandatorsInitialLoading,
        isCostCentersInitialLoading,
        isCreatingOrder,
        assetsData,
        onCancel,
        createNewCostCenter,
        isCreatingNewCostCenter,
        stepTitles: {
            mandator: tTerminology('mandator'),
            asset: t('steps.asset'),
            valuation: t('steps.valuation'),
        },
    })

    const currentStep = activeStep < steps.length ? steps[activeStep] : undefined

    return (
        <PageGridContainer withBottomSpace withFooter>
            <SuccessBackdrop
                open={!!newOrderId}
                lottieAnimation={CreateOrderSuccessLottie}
                successMsg={t('successMessage')}
                nextIcon={<DocumentIcon />}
                nextMsg={t('successAction')}
                nextAction={closeSuccessMsg}
            />
            <PageGridItem columnSpan={6} columnSpanXs={4}>
                <Stepper activeStep={activeStep} style={{ marginTop: 48, marginBottom: 16, width: '100%' }}>
                    {steps.map(({ title }, index) => {
                        return (
                            <Step key={title}>
                                <NumberStepLabel step={index} label={title} />
                            </Step>
                        )
                    })}
                </Stepper>
            </PageGridItem>
            {currentStep?.title && (
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    <Headline title={currentStep?.title} variant="headline1" withSpacing />
                </PageGridItem>
            )}
            {isCreateOrderError && (
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    <Alert severity="error" style={{ maxWidth: '50%', marginBottom: tokens.SizingSpacing300 }}>
                        <Typography variant="textS">
                            {t('error.failed')} — <strong>{t('error.retry')}</strong>
                        </Typography>
                        <Typography variant="textS">{t('error.contact')}</Typography>
                        <Typography variant="textS">{t('error.phone')}</Typography>
                        <Typography variant="textS">{t('error.email')}</Typography>
                    </Alert>
                </PageGridItem>
            )}
            {currentStep?.element}
        </PageGridContainer>
    )
}

/**
 * Since the steps work on the same object, we pass the order and dispatch function to each step and
 * mutate a single state through dispatching actions. The final step has a submit button that calls
 * the mutation on the backend using the single order object.
 */
const getSteps = ({
    onSubmit,
    isCreatingOrder,
    onCancel,
    assetsData,
    prefilledCostCenters,
    prefilledMandators,
    isMandatorsInitialLoading,
    isCostCentersInitialLoading,
    createNewCostCenter,
    isCreatingNewCostCenter,
    stepTitles,
    customerUsers,
    isCustomerUsersPending,
    ...props
}: Omit<StepProps, 'isLastStep'> & {
    onSubmit: OnSubmit
    prefilledCostCenters: CostCenter[]
    prefilledMandators: Mandator[]
    customerUsers: CustomerUser[]
    isMandatorsInitialLoading?: boolean
    isCostCentersInitialLoading?: boolean
    isCreatingOrder?: boolean
    isCustomerUsersPending?: boolean
    assetsData: AssetsData
    createNewCostCenter: (newCostCenter: Omit<CostCenter, 'uuid'>) => void
    isCreatingNewCostCenter?: boolean
    onCancel: () => void
    stepTitles: Record<'mandator' | 'asset' | 'valuation', string>
}) => [
    {
        title: stepTitles.mandator,
        element: (
            <MandatorStep
                prefilledMandators={prefilledMandators}
                isMandatorsPending={isMandatorsInitialLoading}
                onCancel={onCancel}
                {...props}
            />
        ),
    },
    {
        title: stepTitles.asset,
        element: <AssetStep assetsData={assetsData} {...props} />,
    },
    {
        title: stepTitles.valuation,
        element: (
            <OrderValuationStep
                {...props}
                onSubmit={onSubmit}
                isLastStep
                isCreatingOrder={isCreatingOrder}
                customerUsers={customerUsers}
                isCustomerUsersPending={isCustomerUsersPending}
                costCenterOptions={prefilledCostCenters}
                createNewCostCenter={createNewCostCenter}
                isCreatingNewCostCenter={isCreatingNewCostCenter}
                isCostCentersPending={isCostCentersInitialLoading}
                assetsData={assetsData}
            />
        ),
    },
]
